import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../../../../models/Logging';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { copyToClipboard } from '../../../../models/UiUtils';
import { AccountOperationsViewModel, AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { DeprecatedCloseButton } from '../../../../web/components/DeprecatedCloseButton';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { TextInput } from '../../../../web/components/TextInput';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { ConditionalAccess } from '../../ConditionalAccess';
import { AccountDetailsInfoField } from '../AccountDetailsInfoField';
import { useAdminAccountWebsiteUrlMutation } from '../../../../queries/Admin/Account/useAdminAccountWebsiteUrlMutation';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	accountOperations: AccountOperationsViewModel;
}

export function WebsiteUrlField({ className, styles, accountOperations }: IProps) {
	const [urlValue, setUrlValue] = React.useState('');
	const [isEditing, setIsEditing] = React.useState(false);

	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('WebsiteUrlField');
	const websiteUrlMutation = useAdminAccountWebsiteUrlMutation();

	const websiteUrl = accountOperations?.account?.additionalInfo?.websiteUrl;
	const isBusy = websiteUrlMutation.isPending || accountOperations?.isBusy;

	const handleValueChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setUrlValue(e.target.value);
	};

	const handleCopyUrl = () => {
		if (websiteUrl && copyToClipboard(websiteUrl)) {
			toast.success(`${websiteUrl} copied to your clipboard.`);
		}
	};

	const handleStartEdit = () => {
		setUrlValue(websiteUrl || '');
		setIsEditing(true);
	};

	const handleCancelEdit = () => {
		setUrlValue(websiteUrl || '');
		setIsEditing(false);
	};

	const handleSave = async () => {
		const accountId = accountOperations?.account?.id;
		if (!accountId) return;

		try {
			await websiteUrlMutation.mutateAsync({
				accountId,
				websiteUrl: urlValue || '',
			});

			// Update the account operations with the new URL
			if (accountOperations?.account?.additionalInfo) {
				accountOperations.account.additionalInfo.websiteUrl = urlValue || null;
			}

			toast.success('Website URL saved');
			setIsEditing(false);
		} catch (error) {
			errorMessages.pushApiError(error);
			logApiError('UpdateWebsiteUrl-Error', error);
		}
	};

	return (
		<AccountDetailsInfoField
			label='Website URL'
			className={`website-url-field ${css(styleSheet.label)} ${css(...(styles || []))} ${className || ''}`}
		>
			<ConditionalAccess permission={AdminPermission.ModifyAccount} elseChildren={null}>
				{isEditing ? (
					<div className={css(baseStyleSheet.horizontalStack)}>
						<DeprecatedCloseButton disabled={isBusy} onClick={handleCancelEdit} />
						<TextInput
							className={css(styleSheet.input)}
							disabled={isBusy}
							inputId='website-url-field-input'
							onChange={handleValueChanged}
							type='text'
							value={urlValue}
							placeholder='https://example.com'
						/>
						{isBusy ? (
							<LoadingSpinner type='tiny' />
						) : (
							<button className={css(baseStyleSheet.brandLink)} onClick={handleSave}>
								<span>Save</span>
							</button>
						)}
					</div>
				) : (
					<div className={css(!websiteUrl ? styleSheet.linkActions : null)}>
						{websiteUrl ? (
							<>
								<div className={css(baseStyleSheet.truncateText, styleSheet.linkText)}>{websiteUrl}</div>
								<div className={css(baseStyleSheet.horizontalStack, styleSheet.linkActions)}>
									<button className={css(baseStyleSheet.brandLink)} onClick={handleCopyUrl}>
										<span>Copy URL</span>
									</button>
									<button className={css(baseStyleSheet.brandLink)} onClick={handleStartEdit}>
										<span>Edit</span>
									</button>
								</div>
							</>
						) : (
							<button className={css(baseStyleSheet.brandLink)} onClick={handleStartEdit}>
								<span>Add URL</span>
							</button>
						)}
					</div>
				)}
			</ConditionalAccess>
		</AccountDetailsInfoField>
	);
}
